import { useCallback } from 'react';
import useSWR from 'swr';

import {
  IOverwatchActionDefinition,
  IOverwatchActionResult,
  IOverwatchActionSubmission,
  IOverwatchCreateRequest,
  IOverwatchWidgetDefinition,
  IOverwatchWidgetRequest,
  IOverwatchWidgetRequestsResponse,
  OverwatchActionKey,
  OverwatchRequestStatus
} from 'containers/Overwatch/widgets/types';
import { useTypedSelector } from 'hooks';
import API from 'utils/request';

export const OVERWATCH_WIDGETS_PATH = '/v4/dash/overwatch/widgets';
export const OVERWATCH_WIDGET_REQUESTS_PATH = '/v4/dash/overwatch/widget_requests';
export const OVERWATCH_ACTIONS_PATH = '/v4/dash/overwatch/actions';

/**
 * The submit answers only after Zendesk does, within a 30 s budget on the server, so the contract
 * asks for at least 45 s. The shared instance sets no timeout of its own.
 */
export const OVERWATCH_ACTION_TIMEOUT_MS = 45000;

/** A 40932 means the same submission is still running in another request: resend every 2 s, up to 60 s. */
export const OVERWATCH_ACTION_IN_FLIGHT_RETRY_MS = 2000;
export const OVERWATCH_ACTION_IN_FLIGHT_GIVE_UP_MS = 60000;

/** Discovery retries after a network or server failure, with SWR's backoff. */
export const OVERWATCH_ACTIONS_RETRY_COUNT = 3;

export const OVERWATCH_ACTION_CODES = {
  idConflict: 40931,
  inFlight: 40932,
  upstreamFailed: 50231,
  rejected: 50232,
  notConfigured: 50331,
  rateLimited: 50332,
  sourceUnavailable: 50333
} as const;

/** The API caps both `keys` on POST and `ids[]` on poll at 50. */
export const OVERWATCH_MAX_BATCH = 50;

// The API defines no poll cadence or give-up time; these are the frontend's own choice.
export const OVERWATCH_POLL_INTERVAL_MS = 2000;
export const OVERWATCH_POLL_GIVE_UP_MS = 60000;

export const OVERWATCH_REFRESH_WINDOW_MS = 15 * 60 * 1000;
export const OVERWATCH_RATE_LIMIT_CODE = 42901;

export const isTerminalOverwatchStatus = (status: OverwatchRequestStatus | undefined): boolean =>
  status === 'completed' || status === 'failed';

export const chunk = <T>(items: T[], size: number = OVERWATCH_MAX_BATCH): T[][] => {
  const chunks: T[][] = [];

  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }

  return chunks;
};

export type OverwatchFailure =
  | { kind: 'sessionExpired'; message: string | null }
  | { kind: 'forbidden'; message: string | null }
  | { kind: 'invalid'; message: string | null }
  | { kind: 'rateLimited'; message: string | null; retryAfterMs: number }
  | { kind: 'network'; message: string | null }
  | { kind: 'serverError'; message: string | null }
  | { kind: 'technicalError'; message: string | null };

export type OverwatchOutcome<T> = { kind: 'ok'; data: T } | OverwatchFailure;

// The 429 body carries the wait only in prose: "...try again in N seconds".
export const retryAfterMsFromMessage = (message: string | null): number => {
  const match = message?.match(/in (\d+) seconds?/);

  return match ? Number(match[1]) * 1000 : OVERWATCH_REFRESH_WINDOW_MS;
};

export const overwatchFailureFromError = (error: any): OverwatchFailure => {
  const status = error?.response?.status;
  const body = error?.response?.data;
  const message = typeof body?.message === 'string' && body.message ? body.message : null;

  if (error?.isAxiosError === true && error?.response === undefined) {
    return { kind: 'network', message: null };
  }

  if (status === 401) {
    return { kind: 'sessionExpired', message };
  }

  if (status === 403) {
    return { kind: 'forbidden', message };
  }

  if (status === 422) {
    return { kind: 'invalid', message };
  }

  if (status === 429 || body?.code === OVERWATCH_RATE_LIMIT_CODE) {
    return { kind: 'rateLimited', message, retryAfterMs: retryAfterMsFromMessage(message) };
  }

  if (typeof status === 'number' && status >= 500) {
    return { kind: 'serverError', message };
  }

  return { kind: 'technicalError', message };
};

const entriesOf = (response: { data?: IOverwatchWidgetRequestsResponse } | undefined) => {
  const entries = response?.data?.data;

  return Array.isArray(entries) ? entries : null;
};

export const fetchOverwatchWidgets = async (): Promise<IOverwatchWidgetDefinition[]> => {
  const response = await API.get<IOverwatchWidgetDefinition[]>(OVERWATCH_WIDGETS_PATH);

  return Array.isArray(response?.data) ? response.data : [];
};

/** One POST per 50 keys. `date_range` is never sent: every widget owns its reporting window. */
export const createOverwatchWidgetRequests = async ({
  keys,
  params,
  refresh = false
}: IOverwatchCreateRequest): Promise<OverwatchOutcome<IOverwatchWidgetRequest[]>> => {
  try {
    const responses = await Promise.all(
      chunk(keys).map(batch => {
        const batchParams = params
          ? Object.fromEntries(Object.entries(params).filter(([key]) => batch.includes(key)))
          : {};
        const body: IOverwatchCreateRequest = { keys: batch, refresh };

        if (Object.keys(batchParams).length > 0) {
          body.params = batchParams;
        }

        return API.post<IOverwatchWidgetRequestsResponse>(OVERWATCH_WIDGET_REQUESTS_PATH, body);
      })
    );
    const entries = responses.map(entriesOf);

    if (entries.some(batch => batch === null)) {
      return { kind: 'technicalError', message: null };
    }

    return { kind: 'ok', data: (entries as IOverwatchWidgetRequest[][]).flat() };
  } catch (error) {
    return overwatchFailureFromError(error);
  }
};

export const overwatchPollPath = (ids: number[]) =>
  `${OVERWATCH_WIDGET_REQUESTS_PATH}?${ids
    .map(id => `${encodeURIComponent('ids[]')}=${encodeURIComponent(id)}`)
    .join('&')}`;

export const pollOverwatchWidgetRequests = async (
  ids: number[]
): Promise<OverwatchOutcome<IOverwatchWidgetRequest[]>> => {
  try {
    const responses = await Promise.all(
      chunk(ids).map(batch => API.get<IOverwatchWidgetRequestsResponse>(overwatchPollPath(batch)))
    );
    const entries = responses.map(entriesOf);

    if (entries.some(batch => batch === null)) {
      return { kind: 'technicalError', message: null };
    }

    return { kind: 'ok', data: (entries as IOverwatchWidgetRequest[][]).flat() };
  } catch (error) {
    return overwatchFailureFromError(error);
  }
};

export interface UseOverwatchWidgetsResult {
  widgets: IOverwatchWidgetDefinition[] | undefined;
  isLoading: boolean;
  error: any;
}

/** Discovery. The tenant id is part of the key so a tenant switch never reuses another's list. */
export const useOverwatchWidgets = (enabled = true): UseOverwatchWidgetsResult => {
  const tenantId = useTypedSelector(state => state.global?.tenantConfig?.id);
  const { data, isLoading, error } = useSWR<IOverwatchWidgetDefinition[]>(
    enabled && tenantId ? [OVERWATCH_WIDGETS_PATH, tenantId] : null,
    fetchOverwatchWidgets,
    { revalidateOnFocus: false }
  );

  return { widgets: data, isLoading, error };
};

/* ------------------------------------------------------------------ Actions */

export type OverwatchActionFailure =
  | Exclude<OverwatchFailure, { kind: 'rateLimited' | 'invalid' }>
  /** `field`: the path of the first invalid field, such as `input.title`. */
  | { kind: 'invalid'; message: string | null; field: string | null }
  | { kind: 'unknownAction'; message: string | null }
  | { kind: 'idConflict'; message: string | null }
  | { kind: 'inFlight'; message: string | null }
  | { kind: 'notConfigured'; message: string | null }
  | { kind: 'rateLimited'; message: string | null; retryAfterMs: number | null }
  | { kind: 'upstreamFailed'; message: string | null }
  | { kind: 'rejected'; message: string | null }
  | { kind: 'sourceUnavailable'; message: string | null };

/** `data` is null only when a 201 arrives without a body. `cancelled`: the caller aborted the resends. */
export type OverwatchActionOutcome =
  | { kind: 'ok'; data: IOverwatchActionResult | null }
  | { kind: 'cancelled'; message: null }
  | OverwatchActionFailure;

/** `Retry-After` is either delay-seconds or an HTTP date; anything else is an unknown wait. */
export const retryAfterMsFromHeader = (value: unknown, now: number = Date.now()): number | null => {
  const text = typeof value === 'string' ? value.trim() : '';

  if (!text) return null;

  if (/^\d+$/.test(text)) return Number(text) * 1000;

  const date = Date.parse(text);

  return Number.isFinite(date) ? Math.max(0, date - now) : null;
};

const retryAfterHeaderOf = (error: any): unknown => {
  const headers = error?.response?.headers;

  if (typeof headers?.get === 'function') return headers.get('retry-after');

  return headers?.['retry-after'] ?? headers?.['Retry-After'];
};

export const overwatchActionFailureFromError = (error: any): OverwatchActionFailure => {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  const field = error?.response?.data?.field;
  const failure = overwatchFailureFromError(error);
  const { message } = failure;

  if (failure.kind === 'invalid') {
    return { kind: 'invalid', message, field: typeof field === 'string' && field ? field : null };
  }

  if (status === 404) return { kind: 'unknownAction', message };

  if (status === 409 && code === OVERWATCH_ACTION_CODES.idConflict) {
    return { kind: 'idConflict', message };
  }
  if (status === 409 && code === OVERWATCH_ACTION_CODES.inFlight) {
    return { kind: 'inFlight', message };
  }
  if (status === 502 && code === OVERWATCH_ACTION_CODES.upstreamFailed) {
    return { kind: 'upstreamFailed', message };
  }
  if (status === 502 && code === OVERWATCH_ACTION_CODES.rejected) {
    return { kind: 'rejected', message };
  }
  if (status === 503 && code === OVERWATCH_ACTION_CODES.notConfigured) {
    return { kind: 'notConfigured', message };
  }
  if (status === 503 && code === OVERWATCH_ACTION_CODES.sourceUnavailable) {
    return { kind: 'sourceUnavailable', message };
  }
  if (failure.kind === 'rateLimited' || code === OVERWATCH_ACTION_CODES.rateLimited) {
    return {
      kind: 'rateLimited',
      message,
      retryAfterMs: retryAfterMsFromHeader(retryAfterHeaderOf(error))
    };
  }

  return failure;
};

/** Always `{ "data": [...] }`; the list is empty when the features are off or nothing is permitted. */
export const fetchOverwatchActions = async (): Promise<IOverwatchActionDefinition[]> => {
  const response = await API.get<{ data?: unknown }>(OVERWATCH_ACTIONS_PATH);
  const actions = response?.data?.data;

  if (!Array.isArray(actions)) {
    throw new Error('Unexpected Overwatch actions response');
  }

  return actions.filter(action => typeof action?.key === 'string');
};

export type OverwatchActionMap = Record<string, IOverwatchActionDefinition>;

export type OverwatchActionsState =
  | { status: 'loading' }
  | { status: 'ready'; actions: OverwatchActionMap }
  | { status: 'unavailable' };

const fetchOverwatchActionMap = async (): Promise<OverwatchActionMap> =>
  Object.fromEntries((await fetchOverwatchActions()).map(action => [action.key, action]));

// A 4xx answers the same on a retry; while the endpoint is not deployed that is a 404.
const isRetryableDiscoveryError = (error: any) => {
  const status = error?.response?.status;

  return !(typeof status === 'number' && status >= 400 && status < 500);
};

export interface UseOverwatchActionsResult {
  state: OverwatchActionsState;
  /** Resolves with the refetched actions, or the cached ones when the refetch fails. */
  reload: () => Promise<OverwatchActionMap | undefined>;
}

/**
 * Action discovery, shared per tenant by every control that reads it, so a tab switch reuses the
 * answer. A failure reads as `unavailable` and recovers through SWR's capped retry, focus and
 * reconnect; a cached answer is never thrown away by a later failed refetch.
 */
export const useOverwatchActions = (enabled = true): UseOverwatchActionsResult => {
  const tenantId = useTypedSelector(state => state.global?.tenantConfig?.id);
  const { data, error, mutate } = useSWR<OverwatchActionMap>(
    enabled && tenantId ? [OVERWATCH_ACTIONS_PATH, tenantId] : null,
    fetchOverwatchActionMap,
    {
      revalidateIfStale: false,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      shouldRetryOnError: isRetryableDiscoveryError,
      errorRetryCount: OVERWATCH_ACTIONS_RETRY_COUNT
    }
  );
  const reload = useCallback(() => mutate(), [mutate]);

  let state: OverwatchActionsState = { status: 'loading' };

  if (data) {
    state = { status: 'ready', actions: data };
  } else if (error) {
    state = { status: 'unavailable' };
  }

  return { state, reload };
};

const wait = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise<void>(resolve => {
    const timer = setTimeout(done, ms);

    function done() {
      clearTimeout(timer);
      signal?.removeEventListener('abort', done);
      resolve();
    }

    signal?.addEventListener('abort', done);
  });

const postOverwatchAction = async (
  key: OverwatchActionKey,
  submission: IOverwatchActionSubmission
): Promise<OverwatchActionOutcome> => {
  try {
    const response = await API.post<{ data?: IOverwatchActionResult }>(
      `${OVERWATCH_ACTIONS_PATH}/${encodeURIComponent(key)}`,
      submission,
      { timeout: OVERWATCH_ACTION_TIMEOUT_MS }
    );

    // Only a 201 means Zendesk created the ticket.
    if (response?.status !== 201) {
      return { kind: 'technicalError', message: null };
    }

    return { kind: 'ok', data: response.data?.data ?? null };
  } catch (error) {
    return overwatchActionFailureFromError(error);
  }
};

/**
 * Every attempt sends the same `client_request_id` and input, so a resend can never open a second
 * ticket. Only an in-flight answer is resent automatically, until the give-up time. Aborting
 * `signal` stops the resends; a POST already sent is left to finish and its answer reads as
 * `cancelled`.
 */
export const submitOverwatchAction = async (
  key: OverwatchActionKey,
  submission: IOverwatchActionSubmission,
  signal?: AbortSignal
): Promise<OverwatchActionOutcome> => {
  const cancelled: OverwatchActionOutcome = { kind: 'cancelled', message: null };

  if (signal?.aborted) return cancelled;

  const giveUpAt = Date.now() + OVERWATCH_ACTION_IN_FLIGHT_GIVE_UP_MS;
  let outcome = await postOverwatchAction(key, submission);

  while (
    outcome.kind === 'inFlight' &&
    Date.now() + OVERWATCH_ACTION_IN_FLIGHT_RETRY_MS <= giveUpAt
  ) {
    await wait(OVERWATCH_ACTION_IN_FLIGHT_RETRY_MS, signal);

    if (signal?.aborted) return cancelled;

    outcome = await postOverwatchAction(key, submission);
  }

  return signal?.aborted ? cancelled : outcome;
};
